"use client"
import type React from "react"
import { useState, type FormEvent } from "react"
import {
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Row,
  Col,
  FormFeedback,
  Container,
  Card,
  CardBody,
  CardHeader,
} from "reactstrap"

// Import Bootstrap CSS in your layout or globals.css
// import 'bootstrap/dist/css/bootstrap.min.css';

interface FormData {
  firstName: string
  lastName: string
  phone: string
  carYear: string
  carBrand: string
  carModel: string
  mileage: string
  region: string
  delivery: "Pick Up" | "Walk In"
}

interface FormErrors {
  firstName?: string
  lastName?: string
  phone?: string
  carYear?: string
  carBrand?: string
  carModel?: string
  mileage?: string
  region?: string
}

export default function CarInquiryForm() {
  const [formData, setFormData] = useState<FormData>({
    firstName: "",
    lastName: "",
    phone: "",
    carYear: "",
    carBrand: "",
    carModel: "",
    mileage: "",
    region: "",
    delivery: "Pick Up",
  })

  const [errors, setErrors] = useState<FormErrors>({})

  const carBrands = ["Toyota", "Honda", "Nissan", "Ford", "BMW", "Mercedes", "Other"]

  const mileageRanges = [
    { value: "0", label: "10,000 - 20,000 km" },
    { value: "10000", label: "20,000 - 30,000 km" },
    { value: "20000", label: "30,000 - 40,000 km" },
    { value: "30000", label: "40,000 - 50,000 km" },
    { value: "40000", label: "50,000 - 60,000 km" },
    { value: "50000", label: "60,000 - 70,000 km" },
    { value: "60000", label: "70,000 - 80,000 km" },
    { value: "70000", label: "80,000 - 90,000 km" },
    { value: "80000", label: "90,000 - 100,000 km" },
    { value: "90000", label: "100,000 - 120,000 km" },
    { value: "100000", label: "Other" },
  ]

  const regions = [
    { value: "Peninsular Malaysia", label: "Peninsular Malaysia" },
    { value: "Sabah / Sarawak", label: "Sabah / Sarawak" },
  ]

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData({
      ...formData,
      [name]: value,
    })

    // Clear error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors({
        ...errors,
        [name]: undefined,
      })
    }
  }

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {}
    let isValid = true

    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required"
      isValid = false
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last name is required"
      isValid = false
    }

    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required"
      isValid = false
    }

    if (!formData.carYear.trim()) {
      newErrors.carYear = "Car year is required"
      isValid = false
    } else {
      const year = Number.parseInt(formData.carYear)
      if (year < 1990 || year > new Date().getFullYear()) {
        newErrors.carYear = "Year must be between 1990 and current year"
        isValid = false
      }
    }

    if (!formData.carBrand) {
      newErrors.carBrand = "Car brand is required"
      isValid = false
    }

    if (!formData.carModel.trim()) {
      newErrors.carModel = "Car model is required"
      isValid = false
    }

    if (!formData.mileage) {
      newErrors.mileage = "Mileage is required"
      isValid = false
    }

    if (!formData.region) {
      newErrors.region = "Region is required"
      isValid = false
    }

    setErrors(newErrors)
    return isValid
  }

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()

    if (validateForm()) {
      // Format the message for WhatsApp
      const messageLines = [
        "*CC AUTOWORLD WEBSITE*",
        "*Vehicle Inquiry - Looking to Sell*",
        "",
        `First Name: ${formData.firstName}`,
        `Last Name: ${formData.lastName}`,
        `Phone: ${formData.phone}`,
        `Car Year: ${formData.carYear}`,
        `Car Brand: ${formData.carBrand}`,
        `Car Model: ${formData.carModel}`,
        `Mileage: ${mileageRanges.find((m) => m.value === formData.mileage)?.label || formData.mileage}`,
        `Region: ${formData.region}`,
        `Delivery Method: ${formData.delivery}`
      ]
      
      const message = messageLines.join('\n')

      // Encode the message for WhatsApp URL
      const encodedMessage = encodeURIComponent(message)

      // Open WhatsApp with the pre-filled message
      // Note: You would replace this with your actual WhatsApp business number
      window.open(`https://wa.me/601162914913?text=${encodedMessage}`, "_blank")
    }
  }

  return (
    <Container className="h-100">
      <Card className="">
        <CardHeader className="text-center border-bottom-0 pt-4 pb-4" style={{backgroundColor: '#E74B3E'}}>
          <h2 className="mb-2 fw-semibold text-white">Request A Quote</h2>
          <p className="text-white">Complete this form to contact us via WhatsApp</p>
        </CardHeader>
        <CardBody className="p-4">
          <Form onSubmit={handleSubmit}>
            {/* First Row */}
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="firstName">First Name*</Label>
                  <Input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    invalid={!!errors.firstName}
                  />
                  {errors.firstName && <FormFeedback>{errors.firstName}</FormFeedback>}
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="lastName">Last Name*</Label>
                  <Input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    invalid={!!errors.lastName}
                  />
                  {errors.lastName && <FormFeedback>{errors.lastName}</FormFeedback>}
                </FormGroup>
              </Col>
            </Row>

            {/* Second Row */}
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="phone">Phone Number*</Label>
                  <Input
                    type="tel"
                    id="phone"
                    name="phone"
                    placeholder="+6016 xxx xxxx"
                    value={formData.phone}
                    onChange={handleChange}
                    invalid={!!errors.phone}
                  />
                  {errors.phone && <FormFeedback>{errors.phone}</FormFeedback>}
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="carYear">Car Year*</Label>
                  <Input
                    type="number"
                    id="carYear"
                    name="carYear"
                    min={1990}
                    max={new Date().getFullYear()}
                    value={formData.carYear}
                    onChange={handleChange}
                    invalid={!!errors.carYear}
                  />
                  {errors.carYear && <FormFeedback>{errors.carYear}</FormFeedback>}
                </FormGroup>
              </Col>
            </Row>

            {/* Third Row */}
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="carBrand">Car Brand*</Label>
                  <Input
                    type="select"
                    id="carBrand"
                    name="carBrand"
                    value={formData.carBrand}
                    onChange={handleChange}
                    invalid={!!errors.carBrand}
                  >
                    <option value="">Select Brand</option>
                    {carBrands.map((brand) => (
                      <option key={brand} value={brand}>
                        {brand}
                      </option>
                    ))}
                  </Input>
                  {errors.carBrand && <FormFeedback>{errors.carBrand}</FormFeedback>}
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="carModel">Car Model*</Label>
                  <Input
                    type="text"
                    id="carModel"
                    name="carModel"
                    value={formData.carModel}
                    onChange={handleChange}
                    invalid={!!errors.carModel}
                  />
                  {errors.carModel && <FormFeedback>{errors.carModel}</FormFeedback>}
                </FormGroup>
              </Col>
            </Row>

            {/* Fourth Row */}
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="mileage">Mileage (km)*</Label>
                  <Input
                    type="select"
                    id="mileage"
                    name="mileage"
                    value={formData.mileage}
                    onChange={handleChange}
                    invalid={!!errors.mileage}
                  >
                    <option value="">Select Mileage</option>
                    {mileageRanges.map((range) => (
                      <option key={range.value} value={range.value}>
                        {range.label}
                      </option>
                    ))}
                  </Input>
                  {errors.mileage && <FormFeedback>{errors.mileage}</FormFeedback>}
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="region">Region*</Label>
                  <Input
                    type="select"
                    id="region"
                    name="region"
                    value={formData.region}
                    onChange={handleChange}
                    invalid={!!errors.region}
                  >
                    <option value="">Select Region</option>
                    {regions.map((region) => (
                      <option key={region.value} value={region.value}>
                        {region.label}
                      </option>
                    ))}
                  </Input>
                  {errors.region && <FormFeedback>{errors.region}</FormFeedback>}
                </FormGroup>
              </Col>
            </Row>

            {/* Delivery Options */}
            <FormGroup className="mt-3">
              <Label className="fw-medium">Delivery Method:</Label>
              <div className="d-flex mt-2">
                <FormGroup check className="me-4">
                  <Input
                    type="radio"
                    name="delivery"
                    id="pickUp"
                    value="Pick Up"
                    checked={formData.delivery === "Pick Up"}
                    onChange={handleChange}
                  />
                  <Label check for="pickUp">
                    Pick Up
                  </Label>
                </FormGroup>
                <FormGroup check>
                  <Input
                    type="radio"
                    name="delivery"
                    id="walkIn"
                    value="Walk In"
                    checked={formData.delivery === "Walk In"}
                    onChange={handleChange}
                  />
                  <Label check for="walkIn">
                    Walk In
                  </Label>
                </FormGroup>
              </div>
            </FormGroup>

            <Button
              type="submit"
              color="success"
              className="w-100 mt-3 d-flex align-items-center justify-content-center"
              style={{ padding: "0.75rem", backgroundColor: '#E74B3E' }}
            >
              <p className="text-white">
              <svg
                className="me-2"
                style={{marginBottom: 3}}
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="currentColor"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M17.6 6.32A7.85 7.85 0 0 0 12.05 4c-4.33 0-7.85 3.52-7.85 7.85 0 1.38.36 2.73 1.05 3.92l-1.12 4.07 4.17-1.09a7.85 7.85 0 0 0 3.76.96h.01c4.33 0 7.84-3.52 7.84-7.85 0-2.1-.81-4.07-2.29-5.55zm-5.55 12.05h-.01a6.52 6.52 0 0 1-3.32-.91l-.24-.14-2.47.65.66-2.41-.15-.24a6.52 6.52 0 0 1-1-3.47 6.53 6.53 0 0 1 6.53-6.53c1.74 0 3.38.68 4.61 1.91a6.5 6.5 0 0 1 1.91 4.62c0 3.6-2.93 6.53-6.53 6.53zm3.58-4.89c-.2-.1-1.17-.58-1.35-.64-.18-.06-.32-.1-.45.1-.13.2-.51.64-.63.77-.12.13-.23.15-.43.05-.2-.1-.85-.31-1.62-.99-.6-.53-1-1.19-1.12-1.39-.12-.2-.01-.31.09-.41.09-.09.2-.23.3-.35.1-.12.13-.2.2-.33.07-.13.03-.24-.02-.33-.05-.1-.45-1.08-.62-1.48-.16-.39-.33-.33-.45-.34-.12-.01-.25-.01-.38-.01-.13 0-.34.05-.52.25-.18.2-.68.67-.68 1.63 0 .96.7 1.89.8 2.02.1.13 1.4 2.13 3.39 2.99.47.2.84.32 1.13.41.48.15.91.13 1.25.08.38-.06 1.17-.48 1.33-.94.16-.46.16-.86.11-.94-.05-.08-.19-.13-.39-.23z" />
              </svg>
              Send via WhatsApp
              </p>
            </Button>
          </Form>
        </CardBody>
      </Card>
    </Container>
  )
}
